import { useState, useEffect, useCallback, useRef } from 'react';
import ChatService from '../services/ChatService';
import ApiService from '../services/ApiService';
import browserCacheService from '../services/BrowserCacheService';
import { normalizeProfiles } from '../utils/profileNormalize';

const apiServiceInstance = new ApiService();

const isDev = process.env.NODE_ENV === 'development';
const debugLog = isDev ? console.log.bind(console) : () => { };
const REFRESH_COOLDOWN_MS = 12000;

// Normalisierung lebt seit Oktober 2026 in utils/profileNormalize.js (auch fuer ProfileSheet).

// Ein Listen-Refresh ist kein Erfolgsmoment und bleibt seit Oktober 2026 ohne
// Haptik (vorher 'success' nach jedem Refresh). Die Pull-Schwelle quittiert
// useSheetSwipe bzw. die Pull-Geste selbst.

/**
 * @param {object}  params
 * @param {object}  params.authUser
 * @param {Array}   params.initialProfiles
 * @param {boolean} [params.loadMatchesOnMount=true] - Ob die Match-Liste beim
 *   Mounten geladen wird. Steht die Runden-Ansicht (useMatchRound) aktiv, wird
 *   hier `false` uebergeben: der Hook macht dann keinen /discover-Aufruf mehr,
 *   bleibt aber fuer searchResults, schoolProfiles und handleSearchResults
 *   nutzbar — die braucht AdvancedSearch weiterhin. Vorgabe `true` = bisheriges
 *   Verhalten, damit alle bestehenden Aufrufer unveraendert laufen.
 */
export const useProfileLoader = ({ authUser, initialProfiles, loadMatchesOnMount = true }) => {
  const [potentialMatches, setPotentialMatches] = useState([]);
  const [schoolProfiles, setSchoolProfiles] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [ratedProfiles, setRatedProfiles] = useState(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [hasMoreProfiles, setHasMoreProfiles] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [totalProfileCount, setTotalProfileCount] = useState(0);

  const initialLoadDoneRef = useRef(false);
  const lastLoadedUserIdRef = useRef(null);
  const topMatchesLoadInFlightRef = useRef(false);
  const refreshCooldownRef = useRef(0);
  const refreshInFlightRef = useRef(false);
  // Refs fuer stabile Closures in loadTopMatches
  const schoolProfilesRef = useRef(schoolProfiles);
  const potentialMatchesRef = useRef(potentialMatches);
  const ratedProfilesRef = useRef(ratedProfiles);
  const totalProfileCountRef = useRef(totalProfileCount);

  useEffect(() => { schoolProfilesRef.current = schoolProfiles; }, [schoolProfiles]);
  useEffect(() => { potentialMatchesRef.current = potentialMatches; }, [potentialMatches]);
  useEffect(() => { ratedProfilesRef.current = ratedProfiles; }, [ratedProfiles]);
  useEffect(() => { totalProfileCountRef.current = totalProfileCount; }, [totalProfileCount]);

  const updateMatches = useCallback((profilesParam = null) => {
    let profiles = profilesParam;

    if (!profiles) {
      if (schoolProfilesRef.current.length > 0) {
        profiles = schoolProfilesRef.current;
      } else if (initialProfiles && initialProfiles.length > 0) {
        profiles = initialProfiles;
      }
    }

    if (!profiles || !profiles.length || !authUser) {
      setPotentialMatches([]);
      return;
    }

    const currentRatedProfiles = ratedProfilesRef.current;
    const filteredProfiles = profiles
      .filter(profile => profile.id !== authUser?.id && !currentRatedProfiles.has(profile.id));

    const sortedProfiles = [...filteredProfiles].sort((a, b) => {
      const scoreA = a.matchScore || 0;
      const scoreB = b.matchScore || 0;

      if (scoreA !== scoreB) {
        return scoreB - scoreA;
      }
      return (a.name || '').localeCompare(b.name || '');
    });

    const currentMatches = potentialMatchesRef.current;
    const isSameData = currentMatches.length === sortedProfiles.length
      && currentMatches.every((profile, index) => (
        profile?.id === sortedProfiles[index]?.id
        && (profile?.matchScore || 0) === (sortedProfiles[index]?.matchScore || 0)
        && (profile?.connectionStatus || null) === (sortedProfiles[index]?.connectionStatus || null)
      ));

    if (isSameData) {
      debugLog('\u23ED\uFE0F Skip setPotentialMatches - gleiche Reihenfolge/Score');
      return;
    }

    setPotentialMatches(sortedProfiles);
  }, [authUser?.id, initialProfiles]); // PERFORMANCE: Nur authUser.id statt ganzes Objekt, verhindert Re-Render bei AuthContext-Updates

  // Stabiler Ref auf die jeweils aktuelle loadTopMatches-Funktion, damit der
  // Hintergrund-Prefetch von Seite 2 sie aufrufen kann, ohne Closure-Zyklus.
  const loadTopMatchesRef = useRef(null);

  // Laedt Seite 2 leise im Hintergrund nach, sobald Seite 1 steht (append+silent).
  // setTimeout(0): erst nachdem der aktuelle Seite-1-Load seinen In-Flight-Guard freigibt.
  const prefetchNextPage = useCallback((hasMore) => {
    if (!hasMore || !loadTopMatchesRef.current) return;
    setTimeout(() => {
      if (loadTopMatchesRef.current) loadTopMatchesRef.current(2, true, true);
    }, 0);
  }, []);

  const loadTopMatches = useCallback(async (page = 1, append = false, silent = false, forceRefresh = false) => {
    let didLoad = false;
    const userId = authUser?.id;
    const useCache = !append && !silent && !forceRefresh;

    if (topMatchesLoadInFlightRef.current) {
      debugLog('\u23ED\uFE0F Skip loadTopMatches - load already in progress');
      return false;
    }

    const cachedData = useCache ? browserCacheService.getProfiles(userId, page) : null;

    if (cachedData && cachedData.profiles?.length) {
      const normalized = normalizeProfiles(cachedData.profiles);

      // Profile sofort rendern \u2014 der Cache enth\u00E4lt bereits den zuletzt bekannten connectionStatus.
      // Dadurch entf\u00E4llt der blockierende await auf den Status-Batch (~200-500ms iOS-Flackern).
      setSchoolProfiles(normalized);
      updateMatches(normalized);
      setHasMoreProfiles(!!cachedData.hasMore);
      setCurrentPage(page);
      const fallbackTotal = totalProfileCountRef.current > 0 ? totalProfileCountRef.current : normalized.length;
      setTotalProfileCount(cachedData.totalCount ?? fallbackTotal);
      setIsLoading(false);
      debugLog('\u26A1 Browser Cache Hit', { page, profileCount: cachedData.profiles.length });

      // Connection-Status nachgelagert (non-blocking).
      // Nur ein zweiter setSchoolProfiles, wenn sich tats\u00E4chlich etwas ge\u00E4ndert hat.
      const cachedUserIds = normalized.map(p => p.userId).filter(Boolean);
      if (cachedUserIds.length > 0) {
        ChatService.getConnectionStatusBatch(cachedUserIds).then(statuses => {
          let changed = false;
          const updated = normalized.map(p => {
            if (p.userId && statuses[p.userId] && statuses[p.userId] !== p.connectionStatus) {
              changed = true;
              return { ...p, connectionStatus: statuses[p.userId] };
            }
            return p;
          });
          if (changed) {
            setSchoolProfiles(updated);
            updateMatches(updated);
          }
        }).catch(err => {
          debugLog('\u26A0\uFE0F Batch Connection-Status fuer Cache fehlgeschlagen:', err);
        });
      }

      // Seite 2 im Hintergrund vorladen, damit Infinite-Scroll sofort gefuellt ist.
      if (page === 1) prefetchNextPage(!!cachedData.hasMore);

      return true;
    }

    topMatchesLoadInFlightRef.current = true;
    try {
      if (!append && !silent) {
        setIsLoading(true);
        setError(null);
      } else if (append) {
        setIsLoadingMore(true);
      }
      if (forceRefresh) {
        setIsRefreshing(true);
        setError(null);
      }

      const result = await apiServiceInstance.getTopMatches(page, 20, {
        minScore: 0
      });

      didLoad = true;

      if (result.profiles && result.profiles.length > 0) {
        const normalized = normalizeProfiles(result.profiles);

        // Profile SOFORT rendern - Connection-Status nachgelagert (non-blocking),
        // damit das erste Bild nicht ~100-200ms auf den Status-Batch wartet.
        if (append) {
          const updatedProfiles = [...schoolProfilesRef.current, ...normalized];
          setSchoolProfiles(updatedProfiles);
          updateMatches(updatedProfiles);
          debugLog(`\uD83D\uDCC4 ${normalized.length} weitere Profile hinzugefuegt`);
        } else {
          setSchoolProfiles(normalized);
          updateMatches(normalized);
          debugLog(`\uD83C\uDD95 ${normalized.length} Profile initial geladen`);
        }

        setHasMoreProfiles(result.hasMore);
        setCurrentPage(result.page);
        setTotalProfileCount(result.totalCount);

        // Cache erst NACH dem (nachgelagerten) Status-Batch schreiben, damit der
        // gecachte Datensatz bereits den connectionStatus enthaelt (wie der Cache-Hit-Pfad erwartet).
        const persistCache = () => {
          if (userId && !append) {
            browserCacheService.setProfiles(userId, page, normalized, result.hasMore, result.totalCount);
          }
        };

        // Connection-Status im Hintergrund nachladen und nur bei Aenderung erneut setzen.
        const userIds = normalized.map(p => p.userId).filter(Boolean);
        if (userIds.length > 0) {
          ChatService.getConnectionStatusBatch(userIds).then(statuses => {
            let changed = false;
            normalized.forEach(p => {
              if (p.userId && statuses[p.userId] && statuses[p.userId] !== p.connectionStatus) {
                p.connectionStatus = statuses[p.userId];
                changed = true;
              }
            });
            if (changed) {
              const refreshed = append ? [...schoolProfilesRef.current] : [...normalized];
              setSchoolProfiles(refreshed);
              updateMatches(refreshed);
            }
            persistCache();
          }).catch(err => {
            debugLog('\u26A0\uFE0F Batch Connection-Status fehlgeschlagen:', err);
            persistCache();
          });
        } else {
          persistCache();
        }

        // Seite 2 im Hintergrund vorladen, damit Infinite-Scroll sofort gefuellt ist.
        if (!append && page === 1) prefetchNextPage(result.hasMore);
      } else {
        if (!append) {
          setSchoolProfiles([]);
          setPotentialMatches([]);
        }
        setHasMoreProfiles(false);
      }
    } catch (loadError) {
      console.error('\u274C FEHLER beim Laden der Top Matches:', loadError);

      if (loadError.message.includes('Authentifizierung fehlgeschlagen') || loadError.response?.status === 401) {
        setError('Authentifizierung fehlgeschlagen - bitte neu anmelden');
      } else {
        setError(`Fehler beim Laden der Profile: ${loadError.message}`);
      }

      if (!append) {
        setSchoolProfiles([]);
        setPotentialMatches([]);
      }
      setHasMoreProfiles(false);
    } finally {
      topMatchesLoadInFlightRef.current = false;
      setIsLoading(false);
      setIsLoadingMore(false);
      if (forceRefresh) {
        setIsRefreshing(false);
      }
    }
    return didLoad;
  }, [authUser?.id, updateMatches, prefetchNextPage]);

  // loadTopMatchesRef stets aktuell halten, damit prefetchNextPage die neueste Version aufruft.
  useEffect(() => { loadTopMatchesRef.current = loadTopMatches; }, [loadTopMatches]);

  const canTriggerRefresh = useCallback(() => {
    if (refreshInFlightRef.current) return false;
    return Date.now() - refreshCooldownRef.current >= REFRESH_COOLDOWN_MS;
  }, []);

  const triggerRefresh = useCallback(async (source = 'manual') => {
    const userId = authUser?.id;
    if (!userId) return;
    // Inline-Check statt canTriggerRefresh() um stale isRefreshing/isLoading zu vermeiden
    if (refreshInFlightRef.current) return;
    if (Date.now() - refreshCooldownRef.current < REFRESH_COOLDOWN_MS) return;

    refreshInFlightRef.current = true;
    refreshCooldownRef.current = Date.now();
    debugLog('\uD83D\uDD04 Refresh triggered', { source });

    try {
      // silent=true: nur isRefreshing toggelt, kein globales isLoading
      // (verhindert Loading-State-Flackern in HomeView waehrend des Pull-Refresh)
      await loadTopMatches(1, false, true, true);
    } finally {
      refreshInFlightRef.current = false;
    }
  }, [authUser?.id, loadTopMatches]);

  const handleLoadMore = useCallback(() => {
    if (hasMoreProfiles && !isLoadingMore && !isLoading) {
      debugLog(`\uD83D\uDCC4 Lade weitere Profile - Seite ${currentPage + 1}`);
      loadTopMatches(currentPage + 1, true);
    }
  }, [hasMoreProfiles, isLoadingMore, isLoading, currentPage, loadTopMatches]);

  const handleSearchResults = useCallback((results) => {
    if (!authUser || !authUser.id) return;

    const filteredResults = results.filter(profile => profile.id !== authUser.id);

    const currentIds = new Set(searchResults.map(p => p.id));
    const newIds = new Set(filteredResults.map(p => p.id));

    let hasChanged = currentIds.size !== newIds.size;

    if (!hasChanged) {
      for (const id of currentIds) {
        if (!newIds.has(id)) {
          hasChanged = true;
          break;
        }
      }
    }

    if (hasChanged) {
      setSearchResults(filteredResults);
    }
  }, [authUser, searchResults]);

  const addRatedProfile = useCallback((profileId) => {
    setRatedProfiles(prev => new Set(prev).add(profileId));
  }, []);

  const clearProfileCache = useCallback(() => {
    const userId = authUser?.id;
    if (userId) {
      browserCacheService.clearUserCache(userId);
      loadTopMatches(1, false, false);
      debugLog('\uD83D\uDDD1\uFE0F Profile-Cache geleert und neu geladen');
    }
  }, [authUser?.id, loadTopMatches]);

  // Initial profile load
  useEffect(() => {
    const schoolId = authUser?.schoolId;
    const userId = authUser?.id;

    if (!loadMatchesOnMount) {
      // Die Runden-Ansicht laedt selbst. Ein zweiter /discover-Aufruf waere
      // reine Netzlast fuer Daten, die niemand anzeigt.
      setIsLoading(false);
      return;
    }

    if (schoolId && userId) {
      if (lastLoadedUserIdRef.current === userId && initialLoadDoneRef.current) {
        debugLog('\u23ED\uFE0F Skip loadTopMatches - bereits fuer diesen User geladen:', userId);
        return;
      }

      debugLog('\u2705 SchoolId und UserId gefunden, lade Top Matches...', { schoolId, userId });
      lastLoadedUserIdRef.current = userId;
      initialLoadDoneRef.current = true;
      loadTopMatches(1, false);
    } else {
      setIsLoading(false);
    }
  }, [authUser?.id, authUser?.schoolId, loadTopMatches, loadMatchesOnMount]);

  return {
    potentialMatches,
    schoolProfiles,
    searchResults,
    isLoading,
    error,
    isRefreshing,
    hasMoreProfiles,
    currentPage,
    isLoadingMore,
    totalProfileCount,
    ratedProfiles,
    handleLoadMore,
    triggerRefresh,
    canTriggerRefresh,
    handleSearchResults,
    addRatedProfile,
    clearProfileCache,
    setIsLoading
  };
};
